import React from 'react';
import { gridRowAnswers, gridRowKey, screenWords, toggleTick } from '../../utils/hfpV4Form';

/*
 * One screen of the v4 form (TDI-525, form specification 4.1 and 10): a
 * fieldset whose legend is the comparison heading plus the question, the
 * prompt for this adult in the same place every time, and native radio
 * buttons or checkboxes with visible labels. A support grid is one screen:
 * one card per support, each its own radio group (specification section 6).
 */

// Working words for the grid and the error line. The model's fixed-lines
// table (D95) has no grid or instruction lines yet; these follow the plain-
// English standard (specification section 10) and go to Jasmine's review.
export const GRID_WORDS = {
  rated: 'Which of these do they have, and how much do they help?',
  plain: 'Which of these do they have?',
  prompt: 'Answer for each one.',
  haveGroup: 'We have this, and it is:',
};

const capitalise = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

function Legend({ id, heading, text }) {
  return (
    <legend id={id} className="hfp4-legend" tabIndex={-1}>
      {heading ? <span className="hfp4-legend__heading">{heading}</span> : null}
      <span className="hfp4-legend__question">{text}</span>
    </legend>
  );
}

function Prompt({ id, text }) {
  if (!text) return null;
  return <p id={id} className="hfp4-prompt">{text}</p>;
}

function ErrorLine({ id, show }) {
  return (
    <p id={id} className="hfp4-error" role="alert" aria-live="assertive">
      {show ? 'Choose an answer, or Not sure' : ''}
    </p>
  );
}

function RadioList({ name, options, value, onChange, describedBy }) {
  return (
    <div className="hfp4-options">
      {options.map((opt) => {
        const id = `${name}--${opt.value}`.replace(/[^A-Za-z0-9_-]/g, '_');
        const checked = value === opt.value;
        return (
          <label
            key={opt.value}
            htmlFor={id}
            className={['hfp4-option', checked ? 'hfp4-option--checked' : '', opt.disabled ? 'hfp4-option--disabled' : '']
              .filter(Boolean).join(' ')}
          >
            <input
              id={id}
              type="radio"
              name={name}
              value={opt.value}
              checked={checked}
              disabled={Boolean(opt.disabled)}
              onChange={() => onChange(opt.value)}
              aria-describedby={describedBy}
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function QuestionScreen({ entry, value, onAnswer, showError }) {
  const { screen } = entry;
  const { heading, text } = screenWords(screen);
  const base = `hfp4-${entry.id}`.replace(/[^A-Za-z0-9_-]/g, '_');
  return (
    <fieldset className="hfp4-fieldset" aria-describedby={`${base}-prompt ${base}-error`}>
      <Legend id={`${base}-legend`} heading={heading} text={text} />
      <Prompt id={`${base}-prompt`} text={screen.prompt} />
      <ErrorLine id={`${base}-error`} show={showError} />
      <RadioList
        name={entry.id}
        options={(screen.answers || []).map((a) => ({ value: a.id, label: a.label }))}
        value={value}
        onChange={(v) => onAnswer({ [entry.id]: v })}
      />
    </fieldset>
  );
}

function StepScreen({ entry, value, onAnswer, showError }) {
  const { step } = entry;
  const { heading, text } = screenWords(step);
  const base = `hfp4-${entry.id}`.replace(/[^A-Za-z0-9_-]/g, '_');
  const describedBy = `${base}-prompt ${base}-error`;
  if (step.kind === 'tick') {
    const ticked = Array.isArray(value) ? value : [];
    return (
      <fieldset className="hfp4-fieldset" aria-describedby={describedBy}>
        <Legend id={`${base}-legend`} heading={heading} text={text} />
        <Prompt id={`${base}-prompt`} text={step.prompt} />
        <ErrorLine id={`${base}-error`} show={showError} />
        <div className="hfp4-options">
          {(step.options || []).map((opt) => {
            const id = `${base}--${opt.id}`.replace(/[^A-Za-z0-9_-]/g, '_');
            const checked = ticked.includes(opt.id);
            return (
              <label key={opt.id} htmlFor={id} className={`hfp4-option ${checked ? 'hfp4-option--checked' : ''}`.trim()}>
                <input
                  id={id}
                  type="checkbox"
                  checked={checked}
                  onChange={() => onAnswer({ [step.id]: toggleTick(ticked, opt.id, step.options) })}
                />
                <span>{opt.text}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }
  return (
    <fieldset className="hfp4-fieldset" aria-describedby={describedBy}>
      <Legend id={`${base}-legend`} heading={heading} text={text} />
      <Prompt id={`${base}-prompt`} text={step.prompt} />
      <ErrorLine id={`${base}-error`} show={showError} />
      <RadioList
        name={step.id}
        options={(step.options || []).map((o) => ({ value: o.id, label: o.text }))}
        value={value}
        onChange={(v) => onAnswer({ [step.id]: v })}
      />
    </fieldset>
  );
}

function GridScreen({ entry, answers, onAnswer, showError, moduleName }) {
  const { grid } = entry;
  const base = `hfp4-${grid.id}-grid`;
  return (
    <div className="hfp4-grid" role="group" aria-labelledby={`${base}-legend`} aria-describedby={`${base}-prompt ${base}-error`}>
      <h2 id={`${base}-legend`} className="hfp4-legend" tabIndex={-1}>
        <span className="hfp4-legend__heading">{capitalise(moduleName)}</span>
        <span className="hfp4-legend__question">{grid.ratingsStep ? GRID_WORDS.rated : GRID_WORDS.plain}</span>
      </h2>
      <p id={`${base}-prompt`} className="hfp4-prompt">{GRID_WORDS.prompt}</p>
      <ErrorLine id={`${base}-error`} show={showError} />
      <div className="hfp4-grid__cards">
        {(grid.rows || []).map((row) => {
          const key = gridRowKey(grid.id, row.id);
          const value = answers[key] || null;
          const options = gridRowAnswers(grid, row.id);
          const before = options.filter((o) => !o.group && options.indexOf(o) === 0);
          const have = options.filter((o) => o.group === 'have');
          const after = options.filter((o) => !o.group && options.indexOf(o) > 0);
          const missing = showError && !value;
          return (
            <fieldset key={row.id} className={`hfp4-card ${missing ? 'hfp4-card--missing' : ''}`.trim()}>
              <legend className="hfp4-card__legend">{row.text}</legend>
              <RadioList name={key} options={before} value={value} onChange={(v) => onAnswer({ [key]: v })} />
              {have.length > 0 ? (
                <div role="group" aria-label="We have this, and it is" className="hfp4-card__have">
                  <p className="hfp4-card__have-label" aria-hidden="true">{GRID_WORDS.haveGroup}</p>
                  <RadioList name={key} options={have} value={value} onChange={(v) => onAnswer({ [key]: v })} />
                </div>
              ) : null}
              <RadioList name={key} options={after} value={value} onChange={(v) => onAnswer({ [key]: v })} />
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}

export default function HfpV4Screen({ entry, answers, onAnswer, showError }) {
  if (!entry) return null;
  if (entry.type === 'grid') {
    return (
      <GridScreen
        entry={entry}
        answers={answers}
        onAnswer={onAnswer}
        showError={showError}
        moduleName={entry.module && entry.module.name}
      />
    );
  }
  if (entry.type === 'step') {
    return <StepScreen entry={entry} value={answers[entry.id]} onAnswer={onAnswer} showError={showError} />;
  }
  return <QuestionScreen entry={entry} value={answers[entry.id]} onAnswer={onAnswer} showError={showError} />;
}
